import { ValidationRuleTriggers } from "./triggers";
import { InputValidationResult, InputValidationResultKind } from "../input";
import { localize } from "client_legacy/ndaw/culture";
import { ConversionError } from "../../Forms/converters";
import { IValidationContext } from ".";
import { isNil } from "client/Misc/utils";

class IsNotEmptyRule extends ValidationRuleTriggers<string | nil | ReadonlyArray<unknown>> {
	private readonly _alternativeMessage: string | undefined;

	constructor(alternativeMessage?: string) {
		super();
		this._alternativeMessage = alternativeMessage;
	}

	public validate(value: string | nil | ReadonlyArray<unknown>): InputValidationResult {
		if (isNil(value)) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		if (typeof value === "string") {
			return this.validateString(value);
		}

		return this.validateCollection(value);
	}

	private validateString(value: string): InputValidationResult {
		if (value.trim() === "") {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: this._alternativeMessage || localize("_CannotBeEmpty")
			};
		}

		return {
			kind: InputValidationResultKind.Valid
		};
	}

	private validateCollection(value: ReadonlyArray<unknown>): InputValidationResult {
		if (value.length === 0) {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: this._alternativeMessage || localize("_CannotBeEmpty")
			};
		}

		return {
			kind: InputValidationResultKind.Valid
		};
	}
}

class ConversionRule<TValue> extends ValidationRuleTriggers<TValue | ConversionError> {
	public validate(value: TValue | ConversionError<unknown>): InputValidationResult {
		if (value instanceof ConversionError) {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: value.message
			};
		}

		return {
			kind: InputValidationResultKind.Valid
		};
	}
}

type InlineRuleExecutor<TValue> = (value: TValue, validationContext: IValidationContext) => (Promise<InputValidationResult> | InputValidationResult);

class InlineRule<TValue> extends ValidationRuleTriggers<TValue> {
	private readonly _rule: InlineRuleExecutor<TValue>;
	constructor(rule: InlineRuleExecutor<TValue>) {
		super();
		this._rule = rule;
	}

	public validate(value: TValue, validationContext: IValidationContext): Promise<InputValidationResult> | InputValidationResult {
		return this._rule(value, validationContext);
	}
}

export {
	IsNotEmptyRule,
	ConversionRule,
	type InlineRuleExecutor,
	InlineRule
};